/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/
import { logger } from "redux-logger";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { combineReducers, configureStore } from "@reduxjs/toolkit";

import login from "./slices/login";
import app from "./slices/app";
import classes from "./slices/classes";
import dialogues from "./slices/dialogues";
import goals from "./slices/goals";
import intros from "./slices/intros";
import knowledgeComponents from "./slices/knowledgeComponents";
import learnerTypes from "./slices/learnerTypes";
import lessons from "./slices/lessons";
import organizations from "./slices/organizations";
import plans from "./slices/plans";
import surveys from "./slices/surveys";
import topics from "./slices/topics";
import users from "./slices/users";

import gaima from "./slices/gaima";
import state from "./slices/state";

const loginConfig = {
  key: "login",
  storage,
  blacklist: ["user", "loginStatus"],
};

function config(name: string, names?: string) {
  return {
    key: names || `${name}s`,
    storage,
    blacklist: [`${name}LoadStatus`, `${name}Edits`],
  };
}

export const store = configureStore({
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(logger),
  reducer: combineReducers({
    gaima: gaima,
    state: persistReducer(config("state", "state"), state),

    login: persistReducer(loginConfig, login),
    app: persistReducer(config("app", "app"), app),
    classes: persistReducer(config("class", "classes"), classes),
    dialogues: persistReducer(config("dialogue"), dialogues),
    goals: persistReducer(config("goal"), goals),
    intros: persistReducer(config("intro"), intros),
    knowledgeComponents: persistReducer(
      config("knowledgeComponent"),
      knowledgeComponents
    ),
    lessons: persistReducer(config("lesson"), lessons),
    learnerTypes: persistReducer(config("learnerType"), learnerTypes),
    organizations: persistReducer(config("organization"), organizations),
    plans: persistReducer(config("plan"), plans),
    surveys: persistReducer(config("survey"), surveys),
    topics: persistReducer(config("topic"), topics),
    users: persistReducer(config("user"), users),
  }),
  // enhancers: (defaultEhancers) => defaultEhancers.concat(sentryEnhancer),
});

export const persistor = persistStore(store);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;

// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
